import React, { useEffect, useReducer } from 'react';

import _ from 'lodash';
import { IconButton } from '@material-ui/core';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';

import { useValueChangeEffect } from 'hooks';
import { call, classes, toNumber } from 'utils';

import styles from './Orderer.module.scss';

const initState = props => {
  const min = typeof props.min === 'number' && props.min > 0 ? props.min : 0;
  const max = typeof props.max === 'number' && props.max > 0 ? props.max : Infinity;
  const value = Math.min(Math.max(toNumber(props.value), min), max);

  return { value, min, max, out: value };
};

const reducer = (state, action) => {
  const safeValue = value => Math.min(Math.max(toNumber(value), state.min), state.max);

  switch (action.type) {
    case 'STEP': {
      const value = safeValue(state.value + action.payload);
      return value !== state.value ? { ...state, value, out: value } : state;
    }

    case 'PROPS': {
      const newState = initState(action.payload);
      return !_.isEqual(newState, state) ? newState : state;
    }

    case 'VALUE': {
      const value = safeValue(action.payload);
      return value !== state.value ? { ...state, value } : state;
    }

    case 'OUT':
      return state.value !== state.out ? { ...state, out: state.value } : state;

    case 'CANCEL':
      return state.value !== state.out ? { ...state, value: state.out } : state;

    default:
      return state;
  }
};

const Orderer = props => {
  const [{ value, out, min, max }, dispatch] = useReducer(reducer, props, initState);

  useEffect(() => {
    dispatch({
      type: 'PROPS',
      payload: { value: props.value, min: props.min, max: props.max },
    });
  }, [props.value, props.min, props.max]);

  useValueChangeEffect(out, () => call(props.onChange, out));

  const onKeyDown = e => {
    switch (e.key) {
      case 'Enter':
      case 'Escape':
        dispatch({ type: e.key === 'Enter' ? 'OUT' : 'CANCEL' });
        e.target.blur();
        break;

      default:
        break;
    }
  };

  const increment = props.direction === 'up' ? 1 : -1;
  const hasPrevious = increment < 0 ? out > min : out < max;
  const hasNext = increment < 0 ? out < max : out > min;

  if (out !== props.value) {
    return null;
  }

  return (
    <div
      className={classes(
        styles.orderer,
        hasPrevious ? styles.prev : '',
        hasNext ? styles.next : ''
      )}
    >
      <IconButton
        color="primary"
        onClick={() => dispatch({ type: 'STEP', payload: increment })}
      >
        <BsChevronUp />
      </IconButton>

      <input
        value={value}
        onChange={e => dispatch({ type: 'VALUE', payload: e.target.value })}
        onKeyDown={onKeyDown}
        onBlur={() => dispatch({ type: 'OUT' })}
        onFocus={e => e.target.select()}
        onClick={e => e.target.select()}
        disabled={!hasPrevious && !hasNext}
      />

      <IconButton
        color="primary"
        onClick={() => dispatch({ type: 'STEP', payload: -increment })}
      >
        <BsChevronDown />
      </IconButton>
    </div>
  );
};

export default Orderer;
